///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import type { WebcamCategory, WebcamEntry, WebcamStatus, WebcamStreamType } from "@shared-types/webcam"

const cBatchSize = 100

type WebcamRow = {
	id: string
	source_id: string
	name: string
	lat: number
	lon: number
	country_code: string | null
	region: string | null
	category: string
	image_url: string | null
	stream_url: string | null
	stream_type: string | null
	refresh_seconds: number
	status: string
	last_success: string | null
	thumb_url: string
	attribution: string
	attribution_url: string | null
}

function rowToEntry(r: WebcamRow): WebcamEntry {
	return {
		id: r.id,
		source_id: r.source_id,
		name: r.name,
		lat: r.lat,
		lon: r.lon,
		country_code: r.country_code,
		region: r.region,
		category: r.category as WebcamCategory,
		image_url: r.image_url,
		stream_url: r.stream_url,
		stream_type: r.stream_type as WebcamStreamType | null,
		refresh_seconds: r.refresh_seconds,
		status: r.status as WebcamStatus,
		last_success: r.last_success,
		thumb_url: r.thumb_url,
		attribution: r.attribution,
		attribution_url: r.attribution_url,
	}
}

const cSelectColumns = `id, source_id, name, lat, lon, country_code, region, category,
	image_url, stream_url, stream_type, refresh_seconds, status, last_success,
	thumb_url, attribution, attribution_url`

// SQLite's default host-parameter limit is 999 on older builds
const cIdsPerQuery = 400

/**
 * The columns the globe layer keeps for every camera: enough to place,
 * cluster, color and rank a marker. Everything else (names, URLs,
 * attribution) is fetched per camera when it becomes interactive
 */
export interface WebcamCompactRow {
	id: string
	lat: number
	lon: number
	category: WebcamCategory
	has_stream: number
	stale: number
}

export class WebcamStationsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists webcam_stations (
					id text primary key,
					source_id text not null,
					name text not null,
					lat real not null,
					lon real not null,
					country_code text,
					region text,
					category text not null,
					image_url text,
					stream_url text,
					stream_type text,
					refresh_seconds integer not null default 300,
					status text not null default 'active',
					last_success text,
					thumb_url text not null,
					attribution text not null,
					attribution_url text
				)`
			},
			{ sql: "create index if not exists idx_webcam_stations_category on webcam_stations(category)" },
			{ sql: "create index if not exists idx_webcam_stations_source on webcam_stations(source_id)" },
			{ sql: "create index if not exists idx_webcam_stations_lat_lon on webcam_stations(lat, lon)" },
		])
	}

	public static async replaceAll(rows: WebcamEntry[]): Promise<void> {
		await Database.db.exec("delete from webcam_stations", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: WebcamEntry[]): Promise<void> {
		const statement = `insert into webcam_stations (
				id, source_id, name, lat, lon, country_code, region, category,
				image_url, stream_url, stream_type, refresh_seconds, status, last_success,
				thumb_url, attribution, attribution_url
			)
			values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
			on conflict(id) do update set
				source_id = excluded.source_id,
				name = excluded.name,
				lat = excluded.lat,
				lon = excluded.lon,
				country_code = excluded.country_code,
				region = excluded.region,
				category = excluded.category,
				image_url = excluded.image_url,
				stream_url = excluded.stream_url,
				stream_type = excluded.stream_type,
				refresh_seconds = excluded.refresh_seconds,
				status = excluded.status,
				last_success = excluded.last_success,
				thumb_url = excluded.thumb_url,
				attribution = excluded.attribution,
				attribution_url = excluded.attribution_url`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(w => [
				w.id,
				w.source_id,
				w.name,
				w.lat,
				w.lon,
				w.country_code,
				w.region,
				w.category,
				w.image_url,
				w.stream_url,
				w.stream_type,
				w.refresh_seconds,
				w.status,
				w.last_success,
				w.thumb_url,
				w.attribution,
				w.attribution_url,
			]), "low") // ~38k-row ingest: yield to visible-layer reads (see LocationsTable.insert)
		}
	}

	public static async getAll(): Promise<WebcamEntry[]> {
		const rows = await Database.db.exec(
			`select ${cSelectColumns} from webcam_stations order by id`,
			[],
			"low"
		) as WebcamRow[]
		return rows.map(rowToEntry)
	}

	/**
	 * Webcams within a lon/lat bounding box, for viewport-driven loading.
	 * When minLon > maxLon the box crosses the antimeridian and the longitude
	 * test becomes an OR. Non-traffic categories and stream-capable cameras
	 * sort first so the limit trims the least interesting tail (traffic cams
	 * carpet metro areas; scenic/marine/etc are sparse and shouldn't vanish).
	 */
	public static async queryByBBox(
		minLon: number,
		minLat: number,
		maxLon: number,
		maxLat: number,
		limit: number,
		excludeTraffic: boolean
	): Promise<WebcamEntry[]> {
		const lonClause = minLon <= maxLon
			? "lon between ? and ?"
			: "(lon >= ? or lon <= ?)"
		const categoryClause = excludeTraffic ? "and category <> 'traffic'" : ""

		const rows = await Database.db.exec(
			`select ${cSelectColumns} from webcam_stations
			where lat between ? and ? and ${lonClause} ${categoryClause}
			order by
				case when category <> 'traffic' then 0 else 1 end,
				case when stream_url is not null then 0 else 1 end,
				id
			limit ?`,
			[minLat, maxLat, minLon, maxLon, limit],
			"low"
		) as WebcamRow[]
		return rows.map(rowToEntry)
	}

	/**
	 * Top non-traffic webcams globally, for high-altitude views where a bbox
	 * is unreliable. Traffic cams are never visible at that range anyway.
	 */
	public static async queryGlobal(limit: number): Promise<WebcamEntry[]> {
		const rows = await Database.db.exec(
			`select ${cSelectColumns} from webcam_stations
			where category <> 'traffic'
			order by
				case when stream_url is not null then 0 else 1 end,
				id
			limit ?`,
			[limit],
			"low"
		) as WebcamRow[]
		return rows.map(rowToEntry)
	}

	/** Every camera in compact form, for the globe layer's cluster index */
	public static async getAllCompact(): Promise<WebcamCompactRow[]> {
		const rows = await Database.db.exec(
			`select id, lat, lon, category,
				case when stream_url is not null then 1 else 0 end as has_stream,
				case when status = 'stale' then 1 else 0 end as stale
			from webcam_stations order by id`,
			[],
			"low"
		) as WebcamCompactRow[]
		return rows
	}

	/** Full rows for a set of ids (cameras becoming interactive on screen) */
	public static async getByIds(ids: string[]): Promise<WebcamEntry[]> {
		const out: WebcamEntry[] = []
		for (let i = 0; i < ids.length; i += cIdsPerQuery) {
			const batch = ids.slice(i, i + cIdsPerQuery)
			const placeholders = batch.map(() => "?").join(",")
			const rows = await Database.db.exec(
				`select ${cSelectColumns} from webcam_stations where id in (${placeholders})`,
				batch,
				"high"
			) as WebcamRow[]
			for (const r of rows) out.push(rowToEntry(r))
		}
		return out
	}

	public static async getById(id: string): Promise<WebcamEntry | null> {
		const rows = await Database.db.exec(
			`select ${cSelectColumns} from webcam_stations where id = ?`,
			[id],
			"low"
		) as WebcamRow[]
		return rows.length > 0 ? rowToEntry(rows[0]) : null
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from webcam_stations`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}
